import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { SelectColumnFilter, Table } from "../Table/Table";
import { Button, Icon, Popup } from "semantic-ui-react";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import { deleteUser, disableUser, enableUser } from "services/apiMiddleware";
import { getUserManagementTableState } from "store/selectors";

const getColumns = (
  t,
  handleDeleteUserClick,
  handleEditUserClick,
  handleDisableUserClick,
  onFilterChange
) => [
  {
    id: "firstname",
    Header: t("firstname"),
    accessor: (rowData) => {
      const value = rowData.firstName;
      return value;
    },
    Cell: function NameRow({ row }) {
      return (
        <div style={{ opacity: row.original.enabled ? "1" : ".45", wordBreak: "break-all" }}>
          {row.original.firstName}
        </div>
      );
    },
  },
  {
    id: "lastname",
    Header: t("lastname"),
    accessor: (rowData) => {
      const value = rowData.lastName;
      return value;
    },
    Cell: function NameRow({ row }) {
      return (
        <div style={{ opacity: row.original.enabled ? "1" : ".45", wordBreak: "break-all" }}>
          {row.original.lastName}
        </div>
      );
    },
  },
  {
    id: "email",
    Header: t("email"),
    accessor: (rowData) => {
      const value = rowData.email;
      return value;
    },
    Cell: function NameRow({ row }) {
      return (
        <div style={{ opacity: row.original.enabled ? "1" : ".45", wordBreak: "break-all" }}>
          {row.original.email}
        </div>
      );
    },
  },
  {
    id: "phone_number",
    Header: t("phone number"),
    accessor: (rowData) => {
      const value = rowData.phoneNumber;
      return value;
    },
    Cell: function NameRow({ row }) {
      return (
        <div style={{ opacity: row.original.enabled ? "1" : ".45" }}>
          {row.original.phoneNumber}
        </div>
      );
    },
  },
  {
    id: "group",
    Header: t("group"),
    width: 40,
    accessor: (rowData) => {
      const value = rowData.userGroup;
      return value;
    },
    Filter: function selectFilter(props) {
      return (
        <SelectColumnFilter
          {...props}
          t={t}
          onFilterChange={(value, id) => onFilterChange(value, id)}
        />
      );
    },
    Cell: function NameRow({ row }) {
      return (
        <div style={{ opacity: row.original.enabled ? "1" : ".45" }}>{row.original.userGroup}</div>
      );
    },
  },
  {
    id: "active",
    Header: t("active"),
    width: 40,
    accessor: (rowData) => {
      const value = rowData.enabled ? t("yes") : t("no");
      return value;
    },
    Filter: function selectFilter(props) {
      return (
        <SelectColumnFilter
          {...props}
          t={t}
          onFilterChange={(value, id) => onFilterChange(value, id)}
        />
      );
    },
    Cell: function NameRow({ row }) {
      return (
        <div style={{ opacity: row.original.enabled ? "1" : ".45" }}>
          {row.original.enabled ? t("yes") : t("no")}
        </div>
      );
    },
  },
  {
    id: "userActions",
    width: 40,
    Cell: function userActions(props) {
      const { row } = props;
      const user = row.original;
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Button.Group basic size="tiny">
            {!isSSOUser(user.id) && (
              <Popup
                content={t("edit_user_group")}
                trigger={
                  /* Disable button until DeleteCognitoUser is functional. */
                  <Button
                    basic
                    icon
                    size="small"
                    disabled={!user.enabled}
                    onClick={() => handleEditUserClick(user, true)}
                  >
                    <Icon name="edit" />
                  </Button>
                }
              />
            )}
            <Popup
              content={user.enabled ? t("disable_main_user") : t("enable_user")}
              trigger={
                /* Disable button until we have disable user -functionality in backend. */
                <Button basic icon size="small" onClick={() => handleDisableUserClick(user)}>
                  <Icon name={user.enabled ? "ban" : "check circle outline"} />
                </Button>
              }
            />
            <Popup
              content={t("delete_user_and_all_info_related")}
              trigger={
                /* Disable button until DeleteCognitoUser is functional.*/
                <Button
                  basic
                  icon
                  size="small"
                  disabled={user.enabled}
                  onClick={() => handleDeleteUserClick(user)}
                >
                  <Icon name="trash" />
                </Button>
              }
            />
          </Button.Group>
        </div>
      );
    },
  },
];

const isSSOUser = (userId) => {
  return userId.startsWith("bauermediaoutdoor");
};
/** User's with these attribute values are hidden from table. */
const getForbiddenAttributeValues = (currentUserEmail) => [
  { name: "email", value: "user.removed@clearchannel.fi" },
  { name: "email", value: "user.removed@clearchannel.com.pl" },
  { name: "email", value: "user.removed@clearchannel.be" },
  { name: "email", value: "user.removed@clearchannel.se" },
  { name: "email", value: "user.removed@clearchannel.dk" },
  { name: "email", value: "user.removed@clearchannel.no" },
  { name: "email", value: "user.removed@clearchannel.nl" },
  { name: "email", value: "aida.import@clearchannel.fi" },
  { name: "email", value: "aida.import@clearchannel.com.pl" },
  { name: "email", value: "aida.import@clearchannel.be" },
  { name: "email", value: "aida.import@clearchannel.se" },
  { name: "email", value: "aida.import@clearchannel.dk" },
  { name: "email", value: "aida.import@clearchannel.no" },
  { name: "email", value: "aida.import@clearchannel.nl" },
  { name: "email", value: "user.removed@bauermediaoutdoor.com" },
  { name: "email", value: "aida.import@bauermediaoutdoor.com" },
  { name: "email", value: "phasetest-env-testuser@mail.com" },
  { name: "email", value: "phasetest-env-testadmin@mail.com" },
  { name: "email", value: "developer-env-testadmin@mail.com" },
  { name: "email", value: "developer-env-testuser@mail.com" },
  { name: "email", value: "testing-env-testadmin@mail.com" },
  { name: "email", value: "testing-env-testuser@mail.com" },
  { name: "email", value: currentUserEmail },
];

/** Filters users based on attribute values */
const filterUsersBasedOnAttributeValue = (forbiddenAttributeValues, users) =>
  users.filter(
    (user) =>
      // keep user if NONE of the forbidden pairs match
      !forbiddenAttributeValues.some(({ name, value }) => {
        const userVal = user[name];
        return (
          // only compare when the user has that property
          userVal !== undefined && String(userVal).toLowerCase() === value.toLowerCase()
        );
      })
  );

const UserManagementTable = ({ handleEditUserClick, isUserEditing }) => {
  const { filterValues } = useSelector(getUserManagementTableState);
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const skipPageResetRef = useRef();

  const onFilterChange = (value, id) => {
    dispatch.reactTableUserManagement.addOrUpdateFilterValue({ value, id });
  };

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmationModalData, setConfirmationModalData] = useState({
    text: "",
    headerText: "",
    icon: "",
  });
  const [userToBeDeleted, setUserToBeDeleted] = useState(null);
  const [userToBeDisabled, setUserToBeDisabled] = useState(null);
  const currentUserEmail = useSelector((state) => state.activeUser.item.email);

  const forbiddenAttributeValues = getForbiddenAttributeValues(currentUserEmail);

  const users = useSelector((state) => state.users.items);
  const handleDeleteUserClick = (user) => {
    /** User's full name and email. This is shown in confirmation modal. */
    const userFullName = `
      ${user.email}
    `;
    setIsConfirmModalOpen(true);
    setConfirmationModalData({
      text: t("delete_user_modal_text", { userFullName }),
      headerText: t("delete_user_modal_header"),
      icon: "trash",
    });
    setUserToBeDeleted(user);
  };

  const handleDisableUserClick = (user) => {
    const userFullName = `
      ${user.email}
    `;
    setIsConfirmModalOpen(true);
    setConfirmationModalData({
      text: user.enabled
        ? t("disable_user_modal_text", { userFullName })
        : t("enable_user_modal_text", { userFullName }),
      headerText: user.enabled ? t("disable_user_modal_header") : t("enable_user_modal_header"),
      icon: user.enabled ? "ban" : "check circle outline",
    });
    setUserToBeDisabled(user);
  };

  const columns = getColumns(
    t,
    handleDeleteUserClick,
    handleEditUserClick,
    handleDisableUserClick,
    onFilterChange
  );

  /** Filter users based on certain attribute values. */
  const filteredUsers = filterUsersBasedOnAttributeValue(forbiddenAttributeValues, users);

  const handleDeleteUser = async () => {
    dispatch.users.setLoading(true);
    setIsConfirmModalOpen(false);
    setConfirmationModalData({ text: "", headerText: "", icon: "" });

    const userId = userToBeDeleted.id;
    setUserToBeDeleted(null);

    await deleteUser(userId);

    dispatch.users.init();
  };

  const handleDisableUser = async () => {
    dispatch.users.setLoading(true);
    setIsConfirmModalOpen(false);
    setConfirmationModalData({ text: "", headerText: "", icon: "" });

    const userData = {
      username: userToBeDisabled.id,
      UserEmail: userToBeDisabled.email,
    };
    setUserToBeDisabled(null);

    if (userToBeDisabled.enabled) {
      await disableUser(userData);
    } else {
      await enableUser(userData);
    }

    dispatch.users.init();
  };

  const reset = () => {
    setConfirmationModalData({
      text: "",
      headerText: "",
      icon: "",
    });
    setUserToBeDeleted(null);
  };

  return (
    <>
      <Table
        tableClass={`table ${isUserEditing ? "disabled" : "enabled"}`}
        columns={columns}
        data={filteredUsers}
        t={t}
        manualFilters
        skipPageResetRef={skipPageResetRef}
        autoResetFilters={false}
        defaultFilteredFields={filterValues}
      />
      <ConfirmationModal
        data={confirmationModalData}
        open={isConfirmModalOpen}
        setOpen={setIsConfirmModalOpen}
        onSuccess={() => (userToBeDeleted ? handleDeleteUser() : handleDisableUser())}
        dataForOnSuccess={{}}
        onCancel={reset}
      />
    </>
  );
};

export default UserManagementTable;
